/**
 * Expression engine — parametric expressions for feature params & dimensions.
 *
 * Documents carry a named parameter table (name → expression string, e.g.
 * width = "60", holeR = "width/12"). Any numeric feature param can hold an
 * `{ $expr: "width/2" }` marker instead of a number; the evaluator resolves
 * markers against the table each regeneration pass, so editing one parameter
 * re-evaluates every feature that (transitively) uses it — the content hash
 * is computed from RESOLVED values.
 *
 * Grammar: numbers, identifiers, + - * / % ^, parentheses, unary minus,
 * min/max/abs/sqrt/floor/ceil/round, trig in DEGREES (sin/cos/tan/asin/
 * acos/atan), constant pi. Pure and dependency-free; unit tested.
 */

/** Marker for an expression-valued numeric param. JSON-friendly. */
export interface Expr {
  readonly $expr: string;
}

export function isExpr(v: unknown): v is Expr {
  return (
    typeof v === 'object' &&
    v !== null &&
    typeof (v as Expr).$expr === 'string' &&
    Object.keys(v as object).length === 1
  );
}

export class ExprError extends Error {
  override name = 'ExprError';
}

const DEG = Math.PI / 180;
const FUNCTIONS: Record<string, (...args: number[]) => number> = {
  sin: (x) => Math.sin(x! * DEG),
  cos: (x) => Math.cos(x! * DEG),
  tan: (x) => Math.tan(x! * DEG),
  asin: (x) => Math.asin(x!) / DEG,
  acos: (x) => Math.acos(x!) / DEG,
  atan: (x) => Math.atan(x!) / DEG,
  sqrt: (x) => Math.sqrt(x!),
  abs: (x) => Math.abs(x!),
  floor: (x) => Math.floor(x!),
  ceil: (x) => Math.ceil(x!),
  round: (x) => Math.round(x!),
  min: (...a) => Math.min(...a),
  max: (...a) => Math.max(...a),
};
const CONSTANTS: Record<string, number> = { pi: Math.PI };

type Token =
  | { t: 'num'; v: number }
  | { t: 'ident'; v: string }
  | { t: 'op'; v: string };

function tokenize(src: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < src.length) {
    const c = src[i]!;
    if (/\s/.test(c)) {
      i++;
    } else if (/[0-9.]/.test(c)) {
      const m = /^\d*\.?\d+(?:[eE][+-]?\d+)?/.exec(src.slice(i));
      if (!m) throw new ExprError(`Bad number at "${src.slice(i, i + 8)}"`);
      tokens.push({ t: 'num', v: Number(m[0]) });
      i += m[0].length;
    } else if (/[A-Za-z_]/.test(c)) {
      const m = /^[A-Za-z_][A-Za-z0-9_]*/.exec(src.slice(i))!;
      tokens.push({ t: 'ident', v: m[0] });
      i += m[0].length;
    } else if ('+-*/%^(),'.includes(c)) {
      tokens.push({ t: 'op', v: c });
      i++;
    } else {
      throw new ExprError(`Unexpected character "${c}"`);
    }
  }
  return tokens;
}

/** Evaluate one expression against a scope of parameter values. */
export function evaluateExpr(src: string, scope: Readonly<Record<string, number>> = {}): number {
  const tokens = tokenize(src);
  let pos = 0;
  const peek = (): Token | undefined => tokens[pos];
  const eat = (): Token => {
    const t = tokens[pos++];
    if (!t) throw new ExprError('Unexpected end of expression');
    return t;
  };
  const expectOp = (v: string): void => {
    const t = eat();
    if (t.t !== 'op' || t.v !== v) throw new ExprError(`Expected "${v}"`);
  };

  // additive → multiplicative → power (right-assoc) → unary → atom
  const additive = (): number => {
    let left = multiplicative();
    for (;;) {
      const t = peek();
      if (t?.t === 'op' && (t.v === '+' || t.v === '-')) {
        pos++;
        const right = multiplicative();
        left = t.v === '+' ? left + right : left - right;
      } else return left;
    }
  };
  const multiplicative = (): number => {
    let left = power();
    for (;;) {
      const t = peek();
      if (t?.t === 'op' && (t.v === '*' || t.v === '/' || t.v === '%')) {
        pos++;
        const right = power();
        left = t.v === '*' ? left * right : t.v === '/' ? left / right : left % right;
      } else return left;
    }
  };
  const power = (): number => {
    const base = unary();
    const t = peek();
    if (t?.t === 'op' && t.v === '^') {
      pos++;
      return Math.pow(base, power()); // right-assoc: 2^3^2 = 2^9
    }
    return base;
  };
  const unary = (): number => {
    const t = peek();
    if (t?.t === 'op' && (t.v === '-' || t.v === '+')) {
      pos++;
      return t.v === '-' ? -unary() : unary();
    }
    return atom();
  };
  const atom = (): number => {
    const t = eat();
    if (t.t === 'num') return t.v;
    if (t.t === 'op' && t.v === '(') {
      const v = additive();
      expectOp(')');
      return v;
    }
    if (t.t === 'ident') {
      const next = peek();
      if (next?.t === 'op' && next.v === '(') {
        const fn = FUNCTIONS[t.v];
        if (!fn) throw new ExprError(`Unknown function "${t.v}"`);
        pos++; // (
        const args: number[] = [];
        if (!(peek()?.t === 'op' && (peek() as { v: string }).v === ')')) {
          args.push(additive());
          while (peek()?.t === 'op' && (peek() as { v: string }).v === ',') {
            pos++;
            args.push(additive());
          }
        }
        expectOp(')');
        return fn(...args);
      }
      if (t.v in scope) return scope[t.v]!;
      if (t.v in CONSTANTS) return CONSTANTS[t.v]!;
      throw new ExprError(`Unknown parameter "${t.v}"`);
    }
    throw new ExprError(`Unexpected "${(t as { v: unknown }).v}"`);
  };

  const value = additive();
  if (pos !== tokens.length) throw new ExprError('Trailing input after expression');
  if (!Number.isFinite(value)) throw new ExprError('Expression is not finite');
  return value;
}

/** Names an expression may reference (validation / dependency tracking). */
export function referencedNames(src: string): string[] {
  try {
    return [
      ...new Set(
        tokenize(src)
          .filter((t, i, arr) => {
            if (t.t !== 'ident') return false;
            const next = arr[i + 1];
            return !(next?.t === 'op' && next.v === '('); // skip function names
          })
          .map((t) => (t as { v: string }).v)
          .filter((n) => !(n in CONSTANTS)),
      ),
    ];
  } catch {
    return [];
  }
}

export interface ParamTableResult {
  /** Successfully evaluated parameter values. */
  values: Record<string, number>;
  /** name → error message for parameters that failed (cycle, bad ref, syntax). */
  errors: Record<string, string>;
}

/**
 * Evaluate the whole parameter table. Parameters may reference each other
 * in any definition order; cycles are reported as errors, not hangs.
 */
export function evalParamTable(params: Readonly<Record<string, string>>): ParamTableResult {
  const values: Record<string, number> = {};
  const errors: Record<string, string> = {};
  const visiting = new Set<string>();

  const resolve = (name: string): number => {
    if (name in values) return values[name]!;
    if (name in errors) throw new ExprError(errors[name]!);
    if (visiting.has(name)) throw new ExprError(`Circular parameter reference through "${name}"`);
    if (!(name in params)) throw new ExprError(`Unknown parameter "${name}"`);
    visiting.add(name);
    try {
      const scope = new Proxy({} as Record<string, number>, {
        has: (_t, key) => typeof key === 'string' && (key in params || key in values),
        get: (_t, key) => (typeof key === 'string' ? resolve(key) : undefined),
      });
      const v = evaluateExpr(params[name]!, scope);
      values[name] = v;
      return v;
    } catch (err) {
      errors[name] = err instanceof Error ? err.message : String(err);
      throw err;
    } finally {
      visiting.delete(name);
    }
  };

  for (const name of Object.keys(params)) {
    try {
      resolve(name);
    } catch {
      /* recorded in errors */
    }
  }
  return { values, errors };
}

/**
 * Deep-resolve `{ $expr }` markers in a params object to numbers.
 * Returns the input unchanged (same reference) when there is nothing to
 * resolve, so hashing and caching see identical objects for plain params.
 */
export function resolveExprs<T>(value: T, scope: Readonly<Record<string, number>>): T {
  if (value === null || typeof value !== 'object') return value;
  if (isExpr(value)) {
    try {
      return evaluateExpr(value.$expr, scope) as unknown as T;
    } catch (err) {
      throw new ExprError(
        `"${value.$expr}": ${err instanceof Error ? err.message : String(err)}`,
      );
    }
  }
  if (ArrayBuffer.isView(value)) return value;
  let changed = false;
  if (Array.isArray(value)) {
    const next = value.map((v) => {
      const r = resolveExprs(v, scope);
      if (r !== v) changed = true;
      return r;
    });
    return (changed ? next : value) as T;
  }
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(value)) {
    const r = resolveExprs(v, scope);
    if (r !== v) changed = true;
    out[k] = r;
  }
  return (changed ? out : value) as T;
}

/** Does this params object contain any `{ $expr }` markers? */
export function containsExprs(value: unknown): boolean {
  if (value === null || typeof value !== 'object') return false;
  if (isExpr(value)) return true;
  if (ArrayBuffer.isView(value)) return false;
  return Object.values(value).some(containsExprs);
}
